import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_URL, { isProdEnv, secretEncryptionKey } from '../../ServiceModule/Service';
import encryptJSON from '../../ServiceModule/Encrypt';
import moment from 'moment-timezone';
// Async Thunk for login
export const loginUser = createAsyncThunk('auth/login', async (userData, { rejectWithValue }) => {
  let data = null;
  if (isProdEnv == 'true') {
    const encryptedPayload = await encryptJSON(userData, secretEncryptionKey);
    data = {
      element: encryptedPayload,
    };
  } else {
    data = userData;
  }
  try {
    const response = await axios.post(`${API_URL}/users/userLogin`, data, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    if (
      error.response &&
      error.response.data &&
      typeof error.response.data.message === 'string' &&
      error.response.data.message.includes('Account is locked. Try again later')
    ) {
      return error.response.data;
    } else if (
      error.response &&
      error.response.data &&
      typeof error.response.data.message === 'string' &&
      error.response.data.message.includes(
        'Account is locked. Please contact to system administrator'
      )
    ) {
      return error.response.data;
    } else {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
});

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/users/createUser`, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const registerUserWithRoleUser = createAsyncThunk(
  'auth/registerWithRoleUsr',
  async (userData, { rejectWithValue }) => {
    let data = null;
    if (isProdEnv == 'true') {
      const encryptedPayload = await encryptJSON(userData, secretEncryptionKey);
      data = {
        element: encryptedPayload,
      };
    } else {
      data = userData;
    }
    try {
      const response = await axios.post(`${API_URL}/users/createUserWithRoleUser`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const UserUpdate = createAsyncThunk('auth/update', async (userData, { rejectWithValue }) => {
  try {
    let data = null;
    if (isProdEnv == 'true') {
      const encryptedPayload = await encryptJSON(userData, secretEncryptionKey);
      data = {
        element: encryptedPayload,
      };
    } else {
      data = userData;
    }
    const response = await axios.post(`${API_URL}/users/updateUserEmail`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const UserUpdateWithUserRole = createAsyncThunk(
  'auth/updateUserEmailWithUserRole',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/users/updateUserEmailWithUserRole`, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async Thunk for verifying user with email
export const verifyUserWithEmail = createAsyncThunk(
  'auth/verifyUserWithEmail',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/users/emailVerification`, userData, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async Thunk for logout
// Async Thunk for logout
export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/users/logout`, {}, { withCredentials: true });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || 'An error occurred');
  }
});

// Async Thunk for checking authentication status on load
export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/users/me`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || 'Not authenticated');
  }
});

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: true, // Start as true to wait for checkAuth check on app mount
    error: null,
    showDialog: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    showDialog: (state, action) => {
      state.showDialog = action.payload;
    },
    clearUserLoginHint: (state) => {
      state.userLogin = null;
      localStorage.removeItem('userLogin');
    },
  },
  extraReducers: (builder) => {
    // Handle checkAuth
    builder.addCase(checkAuth.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(checkAuth.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.status === true) {
        state.isAuthenticated = true;
        state.user = action.payload?.data;
      } else {
        state.isAuthenticated = false;
        state.user = null;
      }
    });
    builder.addCase(checkAuth.rejected, (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
    });

    // Handle login
    builder.addCase(loginUser.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.isAuthenticated = true;
        state.user = action.payload?.data?.user;
      } else {
        state.isAuthenticated = false;
        state.user = null;
      }
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Handle registration
    builder.addCase(registerUser.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.error = null;
      }
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(registerUserWithRoleUser.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.error = null;
      }
    });
    builder.addCase(registerUserWithRoleUser.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Handle user update
    builder.addCase(UserUpdate.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.user = action.payload?.data?.user || action.payload?.data || state.user;
      }
    });
    builder.addCase(UserUpdate.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(UserUpdateWithUserRole.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.user = action.payload?.data?.user || action.payload?.data || state.user;
      }
    });
    builder.addCase(UserUpdateWithUserRole.rejected, (state, action) => {
      state.error = action.payload;
    });

    // Handle email verification
    builder.addCase(verifyUserWithEmail.fulfilled, (state, action) => {
      if (action.payload?.status === true) {
        state.user = action.payload?.data?.user || action.payload?.data || state.user;
      }
    });
    builder.addCase(verifyUserWithEmail.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.isAuthenticated = false;
    });
  },
});

export const { logout, showDialog, clearUserLoginHint } = authSlice.actions;
export default authSlice.reducer;
